/**
 * Every word the app says, in both languages.
 *
 * `satisfies Record<string, Phrase>` is the whole point: a key with no `en` is
 * a compile error, so "the app is fully translated" is something the build
 * proves rather than something somebody checked once.
 *
 * Conventions:
 *  - keys are `area.thing`, and the area matches the screen or component file;
 *  - `{name}` placeholders are filled by `translate(key, lang, vars)`;
 *  - proper nouns are NOT in here. The six game titles, the app's name and
 *    player handles read the same in both languages, so they stay where they
 *    are rather than becoming identical dictionary entries.
 *
 * The English is written to be spoken, not to be a gloss. Where a Filipino
 * table term has a real English term of art the English one is used — bunot is
 * "draw", sapaw is "top", tapon is "discard" — and where the app is being
 * playful the English is playful too rather than literal.
 */

export interface Phrase {
  tl: string;
  en: string;
}

import { DAMA } from './copy.dama.js';
import { PUSOY } from './copy.pusoy.js';
import { MJ } from './copy.mj.js';
import { ACCOUNT } from './copy.account.js';
import { RESULTS } from './copy.results.js';
import { LOBBY } from './copy.lobby.js';
import { ROOM } from './copy.room.js';
import { UI } from './copy.ui.js';
import { TABLE } from './copy.table.js';

/*
 * One dictionary, assembled from a fragment per area so the areas can be
 * written independently. Each fragment carries its own `satisfies`, so a
 * missing translation points at the file that owns the words rather than here.
 */
export const COPY = {
  ...LOBBY,
  ...ACCOUNT,
  ...ROOM,
  ...TABLE,
  ...DAMA,
  ...PUSOY,
  ...MJ,
  ...UI,
  ...RESULTS,
} as const satisfies Record<string, Phrase>;

export type CopyKey = keyof typeof COPY;
